import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PublicFormLayout } from '../components/PublicFormLayout';
import { FormSectionCard } from '../components/FormSectionCard';
import { ProgressSteps } from '../components/ProgressSteps';
import UploadCard from '../components/UploadCard';
import SuccessState from '../components/SuccessState';
import { AlertTriangle, ChevronRight, CheckCircle2, Shield, QrCode, Info } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MEMBER_STEPS = ['Diri', 'Perguruan', 'Hubungan', 'Foto', 'Hantar'];

const FieldWrapper = ({ label, required, hint, children }) => (
  <div>
    <label className="block text-xs font-bold text-[#0B0B0B] uppercase tracking-wide mb-1.5">
      {label}{required && <span className="text-[#D71920] ml-1">*</span>}
    </label>
    {children}
    {hint && <p className="text-xs text-[#999] mt-1">{hint}</p>}
  </div>
);

const StyledInput = (props) => (
  <Input
    {...props}
    className={`h-11 border-[#E5E5E5] focus-visible:ring-1 focus-visible:ring-[#0B0B0B] focus-visible:ring-offset-0 focus-visible:border-[#0B0B0B] text-sm bg-white placeholder:text-[#bbb] ${props.className || ''}`}
  />
);

const StyledSelect = ({ value, onValueChange, placeholder, children }) => (
  <Select value={value} onValueChange={onValueChange}>
    <SelectTrigger className="h-11 border-[#E5E5E5] focus:ring-1 focus:ring-[#0B0B0B] focus:ring-offset-0 text-sm bg-white">
      <SelectValue placeholder={placeholder} />
    </SelectTrigger>
    <SelectContent>{children}</SelectContent>
  </Select>
);

// Right sidebar for member form
const MemberSidebar = () => (
  <div className="space-y-4">
    {/* Process steps */}
    <div className="bg-white border border-[#E5E5E5] rounded overflow-hidden shadow-sm">
      <div className="px-5 py-3 bg-[#0B0B0B] flex items-center gap-2">
        <CheckCircle2 size={14} className="text-[#FFD21F]" />
        <span className="text-xs font-bold text-white uppercase tracking-widest">Proses Semakan</span>
      </div>
      <div className="p-5 space-y-3">
        {[
          { label: 'Dihantar', desc: 'Permohonan diterima oleh sistem' },
          { label: 'Semakan Perguruan', desc: 'Disemak oleh pentadbir perguruan' },
          { label: 'Semakan RSS', desc: 'Pengesahan akhir oleh Sekretariat RSS' },
          { label: 'Aktif', desc: 'Keahlian aktif dan QR card tersedia' },
        ].map((step, i) => (
          <div key={i} className="flex items-start gap-3">
            <div className="w-5 h-5 rounded-full border-2 border-[#E5E5E5] bg-white flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-bold text-[#0B0B0B]">{step.label}</p>
              <p className="text-xs text-[#999]">{step.desc}</p>
            </div>
          </div>
        ))}
      </div>
    </div>

    {/* QR card note */}
    <div className="bg-[#FFFBF0] border border-[#D4AF37]/30 rounded p-5">
      <div className="flex items-center gap-2 mb-2">
        <QrCode size={14} className="text-[#D4AF37]" />
        <span className="text-xs font-bold text-[#0B0B0B] uppercase tracking-wide">Kad Ahli Digital</span>
      </div>
      <p className="text-xs text-[#7a6200] leading-relaxed">
        Kad ahli digital dengan kod QR akan tersedia selepas pendaftaran anda diluluskan sepenuhnya oleh perguruan dan RSS Sarawak.
      </p>
    </div>

    {/* Perguruan note */}
    <div className="bg-white border border-[#E5E5E5] rounded p-5">
      <div className="flex items-center gap-2 mb-2">
        <Info size={14} className="text-[#D71920]" />
        <span className="text-xs font-bold text-[#0B0B0B] uppercase tracking-wide">Perguruan Tidak Tersenarai?</span>
      </div>
      <p className="text-xs text-[#777] leading-relaxed mb-2">
        Jika perguruan anda belum disenaraikan, sila minta wakil perguruan membuat pendaftaran terlebih dahulu.
      </p>
      <a href="/register-school" className="text-xs font-semibold text-[#D71920] hover:underline">
        Daftar Perguruan →
      </a>
    </div>

    {/* Privacy */}
    <div className="bg-[#F7F7F7] border border-[#E5E5E5] rounded p-5">
      <div className="flex items-center gap-2 mb-2">
        <Shield size={13} className="text-[#D71920]" />
        <span className="text-xs font-bold text-[#0B0B0B] uppercase tracking-wide">Privasi Data</span>
      </div>
      <p className="text-xs text-[#777] leading-relaxed">
        Maklumat peribadi anda hanya digunakan untuk tujuan pendaftaran keahlian. Nombor IC tidak akan didedahkan kepada umum.
      </p>
    </div>
  </div>
);

const RegisterMember = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [schools, setSchools] = useState([]);
  const [success, setSuccess] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [consent, setConsent] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    school_id: '',
    full_name: '',
    ic_masked: '',
    dob: '',
    gender: 'Lelaki',
    phone: '',
    address: '',
  });

  useEffect(() => {
    axios.get(`${API_URL}/schools/public`)
      .then(r => setSchools(r.data || []))
      .catch(() => {});
  }, []);

  const update = (field, val) => setFormData(p => ({ ...p, [field]: val }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.school_id) {
      toast.error('Sila pilih perguruan yang berkaitan.');
      return;
    }
    if (!consent) {
      toast.error('Sila tandakan kotak pengesahan sebelum menghantar.');
      return;
    }
    setLoading(true);
    setActiveStep(4);
    try {
      let photo_url = null;
      if (photoFile instanceof File) {
        const fd = new FormData();
        fd.append('file', photoFile);
        const up = await axios.post(`${API_URL}/upload-public?entity_type=member`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        photo_url = up.data.url;
      }
      await axios.post(`${API_URL}/members/public-register`, { ...formData, photo_url });
      setSuccess(true);
      toast.success('Permohonan berjaya dihantar!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Gagal menghantar permohonan');
      setActiveStep(0);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSuccess(false);
    setActiveStep(0);
    setConsent(false);
    setPhotoFile(null);
    setFormData({ school_id: '', full_name: '', ic_masked: '', dob: '', gender: 'Lelaki', phone: '', address: '' });
  };

  if (success) {
    return (
      <PublicFormLayout>
        <SuccessState
          title="Pendaftaran Ahli Berjaya Dihantar"
          message="Maklumat anda telah diterima. Permohonan akan disemak oleh perguruan dan seterusnya oleh Sekretariat Rumpun Silat Sarawak."
          nextSteps={[
            { label: 'Semakan Perguruan', desc: 'Pentadbir perguruan anda akan menyemak permohonan.' },
            { label: 'Pengesahan RSS', desc: 'Sekretariat RSS akan mengesahkan keahlian anda.' },
            { label: 'Keahlian Aktif', desc: 'Kad ahli digital dengan QR code akan tersedia.' },
          ]}
          actions={[
            { label: 'Kembali ke Utama', to: '/', testId: 'success-home-btn' },
            { label: 'Login Portal Ahli', to: '/portal-ahli', testId: 'success-login-btn' },
            { label: 'Daftar Ahli Lain', onClick: resetForm, testId: 'register-another-button' },
          ]}
        />
      </PublicFormLayout>
    );
  }

  return (
    <PublicFormLayout>
      {/* ── Form Hero ────────────────────────────────────── */}
      <section className="bg-white border-b border-[#E5E5E5]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 border border-[#D71920]/30 bg-[#FEF2F2] text-[#D71920] text-[10px] font-bold tracking-widest uppercase px-3 py-1.5 rounded-sm mb-5">
              Pendaftaran Rasmi
            </div>
            <h1 className="text-3xl sm:text-4xl font-extrabold text-[#0B0B0B] mb-3 leading-tight" data-testid="member-form-title">
              Daftar Ahli / Pesilat
            </h1>
            <p className="text-sm sm:text-base text-[#555] mb-6 leading-relaxed">
              Hantar maklumat ahli atau pesilat untuk dikaitkan dengan perguruan berdaftar Rumpun Silat Sarawak.
            </p>
            <div className="flex items-start gap-3 p-4 bg-[#FFFBF0] border border-[#D4AF37]/40 rounded">
              <AlertTriangle size={15} className="text-[#D4AF37] flex-shrink-0 mt-0.5" />
              <p className="text-xs text-[#7a6200] leading-relaxed">
                Semua maklumat yang dihantar akan disemak terlebih dahulu oleh pihak Rumpun Silat Sarawak sebelum disahkan dalam sistem.
              </p>
            </div>

            {/* No schools warning */}
            {schools.length === 0 && (
              <div className="mt-4 flex items-start gap-3 p-4 bg-[#FEF2F2] border border-[#D71920]/20 rounded">
                <AlertTriangle size={15} className="text-[#D71920] flex-shrink-0 mt-0.5" />
                <p className="text-xs text-[#D71920] leading-relaxed">
                  Tiada perguruan aktif tersedia buat masa ini. Sila daftarkan perguruan terlebih dahulu atau hubungi sekretariat.
                </p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ── Progress Steps ───────────────────────────────── */}
      <div className="bg-white border-b border-[#E5E5E5]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4">
          <ProgressSteps steps={MEMBER_STEPS} activeStep={activeStep} />
        </div>
      </div>

      {/* ── Main Form ────────────────────────────────────── */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="grid lg:grid-cols-3 gap-7">

          {/* Left: Form */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="member-registration-form">

              {/* Section 01 — Maklumat Diri */}
              <FormSectionCard number="01" title="Maklumat Diri">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="sm:col-span-2">
                    <FieldWrapper label="Nama Penuh" required>
                      <StyledInput
                        id="full_name"
                        value={formData.full_name}
                        onChange={e => update('full_name', e.target.value)}
                        required
                        placeholder="Nama penuh seperti dalam kad pengenalan"
                        data-testid="input-full-name"
                        onFocus={() => setActiveStep(0)}
                      />
                    </FieldWrapper>
                  </div>
                  <div>
                    <FieldWrapper label="No Kad Pengenalan" hint="Nombor IC tidak akan didedahkan kepada umum.">
                      <StyledInput
                        id="ic_masked"
                        value={formData.ic_masked}
                        onChange={e => update('ic_masked', e.target.value)}
                        placeholder="Contoh: 901231-13-1234"
                        data-testid="input-ic"
                      />
                    </FieldWrapper>
                  </div>
                  <div>
                    <FieldWrapper label="Tarikh Lahir">
                      <StyledInput
                        id="dob"
                        type="date"
                        value={formData.dob}
                        onChange={e => update('dob', e.target.value)}
                        data-testid="input-dob"
                      />
                    </FieldWrapper>
                  </div>
                  <div>
                    <FieldWrapper label="Jantina">
                      <StyledSelect
                        value={formData.gender}
                        onValueChange={val => update('gender', val)}
                        placeholder="Pilih jantina"
                      >
                        <SelectItem value="Lelaki">Lelaki</SelectItem>
                        <SelectItem value="Perempuan">Perempuan</SelectItem>
                      </StyledSelect>
                    </FieldWrapper>
                  </div>
                  <div>
                    <FieldWrapper label="No Telefon">
                      <StyledInput
                        id="phone"
                        value={formData.phone}
                        onChange={e => update('phone', e.target.value)}
                        placeholder="Contoh: 0123456789"
                        data-testid="input-phone"
                      />
                    </FieldWrapper>
                  </div>
                </div>
              </FormSectionCard>

              {/* Section 02 — Perguruan */}
              <FormSectionCard number="02" title="Perguruan" description="Pilih perguruan yang anda daftarkan diri">
                <div className="space-y-4">
                  <FieldWrapper label="Pilih Perguruan" required>
                    <StyledSelect
                      value={formData.school_id}
                      onValueChange={val => { update('school_id', val); setActiveStep(1); }}
                      placeholder="— Pilih perguruan anda —"
                    >
                      {schools.map(s => (
                        <SelectItem key={s.id} value={s.id}>
                          {s.name} · {s.division}, {s.district}
                        </SelectItem>
                      ))}
                    </StyledSelect>
                  </FieldWrapper>
                  <div className="p-4 bg-[#F7F7F7] border border-[#E5E5E5] rounded text-xs text-[#777]">
                    Hanya perguruan yang telah disahkan oleh Sekretariat RSS Sarawak yang tersenarai di sini.
                    Jika perguruan anda belum disenaraikan, sila minta wakil perguruan mendaftar terlebih dahulu.
                  </div>
                </div>
              </FormSectionCard>

              {/* Section 03 — Maklumat Tambahan */}
              <FormSectionCard number="03" title="Maklumat Tambahan" optional>
                <FieldWrapper label="Alamat">
                  <StyledInput
                    id="address"
                    value={formData.address}
                    onChange={e => update('address', e.target.value)}
                    placeholder="Alamat kediaman"
                    data-testid="input-address"
                    onFocus={() => setActiveStep(2)}
                  />
                </FieldWrapper>
              </FormSectionCard>

              {/* Section 04 — Foto */}
              <FormSectionCard number="04" title="Foto Profil" optional>
                <UploadCard
                  value={photoFile}
                  onChange={(f) => { setPhotoFile(f); setActiveStep(3); }}
                  entityType="member"
                  hint="Foto digunakan untuk kad ahli digital anda."
                />
              </FormSectionCard>

              {/* Section 05 — Pengesahan */}
              <FormSectionCard number="05" title="Pengesahan & Penghantaran">
                <div className="space-y-5">
                  <label className="flex items-start gap-3 cursor-pointer group" data-testid="consent-checkbox-label">
                    <input
                      type="checkbox"
                      checked={consent}
                      onChange={e => { setConsent(e.target.checked); setActiveStep(4); }}
                      className="w-4 h-4 mt-0.5 accent-[#0B0B0B] flex-shrink-0 cursor-pointer"
                      data-testid="consent-checkbox"
                    />
                    <span className="text-sm text-[#555] leading-relaxed group-hover:text-[#0B0B0B] transition-colors">
                      Saya mengesahkan bahawa maklumat yang dihantar adalah benar dan bersetuju untuk disemak oleh perguruan dan <strong>Rumpun Silat Sarawak</strong>.
                    </span>
                  </label>

                  <button
                    type="submit"
                    disabled={loading || !consent || schools.length === 0}
                    className="w-full h-12 bg-[#FFD21F] hover:bg-[#f5c800] disabled:bg-[#E5E5E5] disabled:text-[#999] disabled:cursor-not-allowed text-[#0B0B0B] font-extrabold text-sm rounded transition-all flex items-center justify-center gap-2"
                    data-testid="submit-button"
                  >
                    {loading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-[#0B0B0B]/20 border-t-[#0B0B0B] rounded-full animate-spin" />
                        Menghantar...
                      </>
                    ) : (
                      <>
                        Hantar Pendaftaran Ahli
                        <ChevronRight size={16} />
                      </>
                    )}
                  </button>

                  <p className="text-xs text-center text-[#999]">
                    Sudah menjadi ahli?{' '}
                    <a href="/portal-ahli" className="text-[#D71920] font-semibold hover:underline">
                      Log masuk di sini
                    </a>
                  </p>
                </div>
              </FormSectionCard>

            </form>
          </div>

          {/* Right: Sidebar */}
          <div className="lg:col-span-1">
            <div className="lg:sticky lg:top-24">
              <MemberSidebar />
            </div>
          </div>
        </div>
      </div>
    </PublicFormLayout>
  );
};

export default RegisterMember;
